// src/modules/app-views/leads/pages/Document/tabs/RecordTab/LeadPayloadCard.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Empty } from 'antd';
import translateX from 'utils/translateX';
import { useDocumentContext } from 'components/core/DocumentProvider';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const LeadPayloadContent = ({ lead: leadProp }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data: leadFromContext } = useDocumentContext();
  const lead = leadProp !== undefined ? leadProp : leadFromContext;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // rawPayload = full Firestore snapshot (new leads); customData = LP-specific fields (legacy)
  const raw = lead?.rawPayload || lead?.customData;
  const hasData = raw && typeof raw === 'object' && Object.keys(raw).length > 0;
  const formatted = hasData ? JSON.stringify(deepParseStrings(raw), null, 2) : null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={translateX('no_payload_data')}
      />
    );
  }

  return (
    <pre
      style={{
        margin: 0,
        padding: '16px 20px',
        borderRadius: 6,
        fontSize: 12,
        lineHeight: 1.7,
        overflowX: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        // IDE-style: dark background with light text regardless of app theme
        backgroundColor: '#283142',
        color: '#d4d4d4',
        fontFamily:
          "'Fira Code', 'Cascadia Code', 'JetBrains Mono', Menlo, Monaco, Consolas, 'Courier New', monospace",
        textDecoration: 'none',
      }}
    >
      <code
        style={{
          color: 'inherit',
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          fontSize: 'inherit',
          fontFamily: 'inherit',
          textDecoration: 'none',
          borderRadius: 0,
        }}
      >
        {formatted}
      </code>
    </pre>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default LeadPayloadContent;

// *******************************************************************************
// HELPERS
// *******************************************************************************

// Recursively walk an object/array and try to JSON.parse any string values.
// If a string parses successfully to an object or array, replace it with the parsed value.
function deepParseStrings(value) {
  if (Array.isArray(value)) {
    return value.map(deepParseStrings);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, deepParseStrings(v)])
    );
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        const parsed = JSON.parse(trimmed);
        // Recurse in case the parsed value also contains stringified fields
        return deepParseStrings(parsed);
      } catch {
        // Not valid JSON, return original string
      }
    }
  }

  return value;
}
